import { BaseTable, Dot } from '@/components';
import { PRIMARY_COLOR, TEXT_PRIMARY } from '@/constants/common';
import { useBreadcrumbs, useConfirm, useFormUnsavedChanges, useLoadingOverlay } from '@/hooks';
import { trans } from '@/i18n';
import { ROUTE_PATH } from '@/routes';
import { ClientService } from '@/services/client';
import { PlanService } from '@/services/plan';
import {
  ClientSalon,
  ClientWithSalons,
  IColumn,
  IPlanAttachment,
  IPlanForm,
  IPlanFormData,
} from '@/types';
import { showError, showSuccess } from '@/utils';
import { Badge, Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi';
import { useNavigate, useParams } from 'react-router-dom';
import { PlanForm, PlanPreview } from './components';
import './styles/create-plan.scss';
import './styles/detail-plan.scss';
import { mapPlanAttachmentsToFileUrls, mapPlanDetailToFormData } from './utils/planDataMapper';
import { NoteService } from '@/services/note';
import { useWindowSize } from '@/hooks/useWindowSize';

function DetailPlanPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [initialData, setInitialData] = useState<IPlanFormData | undefined>();
  const [fileUrls, setFileUrls] = useState<Record<string, { url: string; fileName: string }>>({});
  const [currentFormData, setCurrentFormData] = useState<IPlanFormData | undefined>();
  const [images, setImages] = useState<IPlanAttachment[]>([]);
  const [clientSalons, setClientSalons] = useState<ClientSalon[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [isDataInitialized, setIsDataInitialized] = useState(false);
  const { toggleConfirm } = useConfirm();
  const { setBreadcrumbs } = useBreadcrumbs();
  const isMac = useWindowSize();
  const { toggleLoading } = useLoadingOverlay();

  const svRef = useRef<PlanService>();
  const clientSvRef = useRef<ClientService>();
  const noteSvRef = useRef<NoteService>();
  if (!svRef.current) {
    svRef.current = new PlanService();
  }
  if (!clientSvRef.current) {
    clientSvRef.current = new ClientService();
  }
  if (!noteSvRef.current) {
    noteSvRef.current = new NoteService();
  }

  const transformApiDataToTableData = useCallback(
    (apiData: { clients: ClientWithSalons[] }): ClientSalon[] => {
      const result: ClientSalon[] = [];

      apiData.clients.forEach((client) => {
        const clientSalon: ClientSalon = {
          id: client.id,
          name: client.name,
          type: 'client',
          status: client.isActive ? 'active' : 'inactive',
          subdomain: '-',
          plans: [],
          isSelected: false,
          isExpanded: true,
          children: [],
        };

        if (client.salons && client.salons.length > 0) {
          client.salons.forEach((salon) => {
            const salonSalon: ClientSalon = {
              id: salon.id,
              name: salon.name,
              type: 'salon',
              status: salon.isActive ? 'active' : 'inactive',
              subdomain: salon.subDomain || '-',
              plans: salon.pages
                .filter((page) => page.plan && page.plan.name)
                .map((page) => page.plan!.name),
              projectNumber: salon.pages?.find((page) => page.planId === id)?.plan?.projectNumber || 0
            };
            clientSalon.children!.push(salonSalon);
          });
        }

        result.push(clientSalon);
      });

      return result;
    },
    []
  );

  const fetchClientsData = useCallback(
    async (planId: string) => {
      try {
        setLoadingClients(true);
        const response = await clientSvRef.current!.getClientsByPlan(planId);
        const transformedData = transformApiDataToTableData(response);
        setClientSalons(transformedData);
      } catch (error) {
        console.error('Error fetching clients data:', error);
        showError('Failed to load clients data');
      } finally {
        setLoadingClients(false);
      }
    },
    [transformApiDataToTableData]
  );

  const flattenedData = useMemo(() => {
    const result: ClientSalon[] = [];
    clientSalons.forEach((client) => {
      result.push(client);
      if (client.isExpanded && client.children) {
        client.children.forEach((child) => {
          result.push({
            ...child,
            name: `  ${child.name}`,
            isChild: true,
          });
        });
      }
    });
    return result;
  }, [clientSalons]);

  const columns: IColumn<ClientSalon>[] = useMemo(
    () => [
      {
        key: 'name',
        title: 'クライアント or サロン',
        render: (item: ClientSalon) => (
          <HStack gap={3}>
            {item.children && item.children.length > 0 && (
              <Box
                cursor="pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand(item.id);
                }}
              >
                {item.isExpanded ? (
                  <HiChevronDown color="gray.400" size={16} />
                ) : (
                  <HiChevronRight color="gray.400" size={16} />
                )}
              </Box>
            )}
            {!item.children && item.isChild && <Box w={4} />}
            <Text
              color="#6A6A6A"
              fontWeight="bold"
              fontSize="14px"
              maxW="250px"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              title={item.name}
            >
              {item.name}
            </Text>
          </HStack>
        ),
        width: '300px',
      },
      {
        key: 'status',
        title: 'ステータス',
        align: 'left',
        render: (item: ClientSalon) => (
          <HStack gap={2}>
            <Dot red={item.status === 'inactive'} />
            <Text color="gray.500" fontWeight="normal" fontSize="14px">
              {item.status === 'active' ? '利用中' : '停止中'}
            </Text>
          </HStack>
        ),
        width: '150px',
      },
      {
        key: 'subdomain',
        title: 'サブドメイン',
        align: 'left',
        render: (item: ClientSalon) => (
          <Text color="gray.500" fontWeight="normal" fontSize="14px">
            {item.subdomain}
          </Text>
        ),
        width: '150px',
      },
      {
        key: 'plans',
        title: 'プラン',
        align: 'left',
        sortable: false,
        render: (item: ClientSalon) => (
          <Box maxW="250px" alignSelf="flex-start" pt={2}>
            {item.plans && item.plans.length > 0 ? (
              <HStack gap={1} flexWrap="wrap" align="flex-start" alignItems="flex-start">
                {item.plans.map((plan, index) => (
                  <Badge
                    key={index}
                    borderRadius="xl"
                    px={2}
                    py={1}
                    fontSize="14px"
                    bg={PRIMARY_COLOR}
                    color="white"
                    mb={1}
                    alignSelf="flex-start"
                    maxW="200px"
                    wordBreak="break-word"
                    whiteSpace="normal"
                    lineHeight="1.2"
                  >
                    {plan}
                  </Badge>
                ))}
              </HStack>
            ) : (
              <Text color="gray.500" fontWeight="normal" fontSize="14px">
                -
              </Text>
            )}
          </Box>
        ),
        width: '250px',
      },
    ],
    []
  );

  const handleToggleExpand = useCallback((clientId: string) => {
    setClientSalons((prev) =>
      prev.map((client) =>
        client.id === clientId ? { ...client, isExpanded: !client.isExpanded } : client
      )
    );
  }, []);

  const handleRowClick = useCallback(
    (clientSalon: ClientSalon) => {
      if (clientSalon.type === 'salon') {
        const subDomain = clientSalon.subdomain;
        const planName = clientSalon.projectNumber;

        const url = `https://${subDomain}.${process.env.REACT_APP_DOMAIN}/lp/${planName}`;

        window.open(url, '_blank');
        return;
      }

      setClientSalons((prev) =>
        prev.map((client) => ({
          ...client,
          isSelected: client.id === clientSalon.id,
        }))
      );
    },
    [clientSalons, navigate]
  );

  useEffect(() => {
    setBreadcrumbs(ROUTE_PATH.PLAN, [
      { name: trans('menu.plan'), path: ROUTE_PATH.PLAN },
      { name: trans('plan.detail') },
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const planDetail = await svRef.current!.getDetail(id);

        const formData = mapPlanDetailToFormData(planDetail);
        const urls = mapPlanAttachmentsToFileUrls(planDetail);

        const imageAttachments = planDetail.planAttachments.filter(
          (pa) =>
            !pa.isArchived &&
            pa.attachment.mimeType.startsWith('image/') &&
            !pa.attachment.fileName.endsWith('.zip')
        );

        setInitialData(formData);
        setCurrentFormData(formData);
        setFileUrls(urls);
        setImages(imageAttachments);

        await fetchClientsData(id);
        setIsDataInitialized(true);
      } catch (error) {
        showError(error);
        navigate(ROUTE_PATH.PLAN);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, fetchClientsData]);

  const hasUnsavedChanges = useMemo(() => {
    if (!currentFormData || !initialData || !isDataInitialized) return false;

    const compareFiles = (
      currentFile: File | undefined,
      initialFile: File | undefined
    ): boolean => {
      if (!currentFile && !initialFile) return true;
      if (!currentFile || !initialFile) return false;
      return currentFile.name === initialFile.name;
    };

    return (
      currentFormData.name !== initialData.name ||
      currentFormData.price !== initialData.price ||
      currentFormData.description !== initialData.description ||
      !compareFiles(currentFormData.htmlFile, initialData.htmlFile) ||
      !compareFiles(currentFormData.cssFile, initialData.cssFile) ||
      !compareFiles(currentFormData.jsFile, initialData.jsFile) ||
      !compareFiles(currentFormData.imageZipFile, initialData.imageZipFile)
    );
  }, [currentFormData, initialData, isDataInitialized]);

  // Use the form unsaved changes hook
  const { resetChanges } = useFormUnsavedChanges(hasUnsavedChanges);

  const debounceTimeoutRef = useRef<NodeJS.Timeout>();

  const handleFormChange = useCallback((data: IPlanFormData) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setCurrentFormData((prevData) => {
        if (!prevData) return data;

        const compareFiles = (
          prevFile: File | undefined,
          currentFile: File | undefined
        ): boolean => {
          if (!prevFile && !currentFile) return true;
          if (!prevFile || !currentFile) return false;
          return prevFile.name === currentFile.name;
        };

        const isEqual =
          prevData.name === data.name &&
          prevData.price === data.price &&
          prevData.description === data.description &&
          compareFiles(prevData.htmlFile, data.htmlFile) &&
          compareFiles(prevData.cssFile, data.cssFile) &&
          compareFiles(prevData.jsFile, data.jsFile) &&
          compareFiles(prevData.imageZipFile, data.imageZipFile);

        return isEqual ? prevData : data;
      });
    }, 50);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleUpdate = useCallback(() => {
    if (!currentFormData || !id) return;

    toggleConfirm({
      title: trans('plan.updateConfirmTitle'),
      message: trans('plan.updateConfirmMessage'),
      okText: trans('plan.yes'),
      cancelText: trans('plan.no'),
      onOk: async () => {
        try {
          toggleLoading(true);

          const changedFiles: Partial<IPlanForm> = {
            name: currentFormData.name?.trim(),
            price: currentFormData.price || 0,
            description: currentFormData.description?.trim(),
          };

          const compareFiles = (
            currentFile: File | undefined,
            initialFile: File | undefined
          ): boolean => {
            if (!currentFile && !initialFile) return true;
            if (!currentFile || !initialFile) return false;
            return currentFile.name === initialFile.name;
          };

          if (!compareFiles(currentFormData.htmlFile, initialData?.htmlFile)) {
            changedFiles.htmlFile = currentFormData.htmlFile;
          }
          if (!compareFiles(currentFormData.cssFile, initialData?.cssFile)) {
            changedFiles.cssFile = currentFormData.cssFile;
          }
          if (!compareFiles(currentFormData.jsFile, initialData?.jsFile)) {
            changedFiles.jsFile = currentFormData.jsFile;
          }
          if (!compareFiles(currentFormData.imageZipFile, initialData?.imageZipFile)) {
            changedFiles.imageZipFile = currentFormData.imageZipFile;
          }

          await svRef.current!.updateData(id, changedFiles);
          showSuccess(trans('plan.updateSuccess'));

          const planDetail = await svRef.current!.getDetail(id);
          const newFormData = mapPlanDetailToFormData(planDetail);
          const newUrls = mapPlanAttachmentsToFileUrls(planDetail);

          const newImageAttachments = planDetail.planAttachments.filter(
            (pa) =>
              !pa.isArchived &&
              pa.attachment.mimeType.startsWith('image/') &&
              !pa.attachment.fileName.endsWith('.zip')
          );

          setInitialData(newFormData);
          setCurrentFormData(newFormData);
          setFileUrls(newUrls);
          setImages(newImageAttachments);
          resetChanges(); // Reset unsaved changes after successful update
        } catch (error) {
          showError(error);
        } finally {
          toggleLoading(false);
        }
      },
    });
  }, [currentFormData, initialData, toggleConfirm, id]);

  const handleDelete = useCallback(() => {
    if (!id) return;

    toggleConfirm({
      title: trans('plan.deleteConfirmTitle'),
      message: trans('plan.deleteConfirmMessage'),
      okText: trans('shared.delete'),
      cancelText: trans('shared.cancelText'),
      onOk: async () => {
        try {
          setLoadingDelete(true);
          const res = await svRef.current!.deleteData(id);
          showSuccess(res.message);
          resetChanges();
          navigate(ROUTE_PATH.PLAN);
        } catch (error) {
          showError(error);
        } finally {
          setLoadingDelete(false);
        }
      },
    });
  }, [toggleConfirm, id, navigate]);

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      toggleConfirm({
        title: trans('plan.unsavedChangesTitle'),
        message: trans('plan.unsavedChangesMessage'),
        okText: trans('plan.leave'),
        cancelText: trans('plan.stay'),
        onOk: () => {
          resetChanges();
          navigate(ROUTE_PATH.PLAN);
        },
      });
    } else {
      navigate(ROUTE_PATH.PLAN);
    }
  }, [hasUnsavedChanges, toggleConfirm, navigate, resetChanges]);

  if (loading && !initialData) {
    return <div>Loading...</div>;
  }

  return (
    <VStack gap={5} align="stretch" className='plan-detail-page'>
      <Flex gap={'41px'} className='plan-detail-page-form'>
        <Box className='plan-detail-page-form-preview' w="461px" minW="461px" h="88vh" bg="white" p={5} borderRadius={15}>
          <PlanPreview
            htmlFile={currentFormData?.htmlFile}
            cssFile={currentFormData?.cssFile}
            jsFile={currentFormData?.jsFile}
            imageZipFile={currentFormData?.imageZipFile}
            fileUrls={fileUrls}
            height="calc(80vh - 40px)"
            images={images}
          />
        </Box>
        <Flex className='plan-detail-page-form-box' gap={5} direction="column" w="full" h="fit-content">
          <Box w="full" h="fit-content" bg="white" p={5} borderRadius={15}>
            <Text fontSize="20px" fontWeight="bold" color={TEXT_PRIMARY} mb={5}>
              プラン情報
            </Text>
            {isDataInitialized && initialData && (
              <PlanForm
                mode="detail"
                initialData={initialData}
                fileUrls={fileUrls}
                onCancel={handleCancel}
                onChange={handleFormChange}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
                loading={loading}
                loadingDelete={loadingDelete}
                hasUnsavedChanges={hasUnsavedChanges}
              />
            )}
          </Box>
          {
            !isMac && (
              <Box className='plan-detail-page-form-table' h="fit-content" bg="white" p={5} borderRadius={15}>
                <BaseTable<ClientSalon>
                  className="plan-table-custom"
                  rowKey="id"
                  columns={columns}
                  loading={loadingClients}
                  data={flattenedData}
                  rowClick={handleRowClick}
                  scroll
                  maxH="22vh"
                  minH="22vh"
                />
              </Box>
            )
          }
        </Flex>
      </Flex>

      {isMac && (
        <Box className='plan-detail-page-form-table' h="fit-content" bg="white" p={5} borderRadius={15}>
          <BaseTable<ClientSalon>
            className="plan-table-custom"
            rowKey="id"
            columns={columns}
            loading={loadingClients}
            data={flattenedData}
            rowClick={handleRowClick}
            scroll
            maxH="22vh"
            minH="22vh"
          />
        </Box>
      )}
    </VStack>
  );
}

export default DetailPlanPage;
